import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Worker setup for react-pdf v10
// ---------------------------------------------------------------------------
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// ---------------------------------------------------------------------------
// Icon helper (Material Symbols Outlined)
// ---------------------------------------------------------------------------
const Icon = ({ name, className }: { name: string; className?: string }) => (
  <span className={cn('material-symbols-outlined', className)}>{name}</span>
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PdfViewerProps {
  fileUrl: string;
  fileName?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ZOOM_STEP = 0.15;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
const ZOOM_DEFAULT = 1;
const ROTATE_STEP = 90;

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp)$/i;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isImageUrl(url: string): boolean {
  try {
    const pathname = new URL(url, 'https://placeholder.local').pathname;
    return IMAGE_EXTENSIONS.test(pathname);
  } catch {
    return IMAGE_EXTENSIONS.test(url);
  }
}

// ---------------------------------------------------------------------------
// Toolbar Button
// ---------------------------------------------------------------------------
function ToolbarButton({
  onClick,
  disabled,
  title,
  children,
  className,
}: {
  onClick?: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'flex items-center justify-center w-8 h-8 rounded-xl',
        'text-calm-muted hover:text-calm-text hover:bg-slate-100',
        'transition-all duration-200 disabled:opacity-30 disabled:pointer-events-none',
        className,
      )}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-calm-border mx-1" />;
}

// ---------------------------------------------------------------------------
// PdfViewer Component
// ---------------------------------------------------------------------------
function PdfViewer({ fileUrl, fileName }: PdfViewerProps) {
  // ---- State ----
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(ZOOM_DEFAULT);
  const [rotation, setRotation] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [fitWidthScale, setFitWidthScale] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isImage = useMemo(() => isImageUrl(fileUrl), [fileUrl]);
  const displayName = fileName || fileUrl.split('/').pop() || 'Documento';

  // ---- Measure container width ----
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // ---- PDF load handlers ----
  const onDocumentLoadSuccess = useCallback(
    ({ numPages: total }: { numPages: number }) => {
      setNumPages(total);
      setCurrentPage(1);
      setIsLoading(false);
      setError(null);
    },
    [],
  );

  const onDocumentLoadError = useCallback((err: Error) => {
    setError(err.message || 'No se pudo cargar el documento PDF.');
    setIsLoading(false);
  }, []);

  // ---- Zoom controls ----
  const handleZoomIn = useCallback(
    () => setScale((s) => Math.min(+(s + ZOOM_STEP).toFixed(2), ZOOM_MAX)),
    [],
  );
  const handleZoomOut = useCallback(
    () => setScale((s) => Math.max(+(s - ZOOM_STEP).toFixed(2), ZOOM_MIN)),
    [],
  );
  const handleZoomReset = useCallback(() => setScale(ZOOM_DEFAULT), []);

  const handleFitWidth = useCallback(() => {
    if (fitWidthScale) {
      setScale(fitWidthScale);
    } else if (containerWidth > 0) {
      // Estimate: default PDF page width is ~612pt (letter)
      const estimatedFit = (containerWidth - 48) / 612;
      setScale(Math.min(Math.max(estimatedFit, ZOOM_MIN), ZOOM_MAX));
    }
  }, [fitWidthScale, containerWidth]);

  // Capture the natural page width to compute fit-width precisely
  const handlePageRenderSuccess = useCallback(() => {
    if (containerWidth > 0 && scrollRef.current) {
      const canvas = scrollRef.current.querySelector('canvas');
      if (canvas) {
        const pageNaturalWidth = canvas.width / scale / (window.devicePixelRatio || 1);
        if (pageNaturalWidth > 0) {
          setFitWidthScale(
            Math.min(Math.max((containerWidth - 48) / pageNaturalWidth, ZOOM_MIN), ZOOM_MAX),
          );
        }
      }
    }
  }, [containerWidth, scale]);

  // ---- Rotation ----
  const handleRotate = useCallback(
    () => setRotation((r) => (r + ROTATE_STEP) % 360),
    [],
  );

  // ---- Page navigation ----
  const goToPrevPage = useCallback(
    () => setCurrentPage((p) => Math.max(p - 1, 1)),
    [],
  );
  const goToNextPage = useCallback(
    () => setCurrentPage((p) => Math.min(p + 1, numPages)),
    [numPages],
  );

  // ---- Download ----
  const handleDownload = useCallback(() => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = displayName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [fileUrl, displayName]);

  // ---- Retry ----
  const handleRetry = useCallback(() => {
    setError(null);
    setIsLoading(true);
    setNumPages(0);
    setCurrentPage(1);
    // Force re-mount by toggling a key — handled by the key prop on Document
  }, []);

  // ---- Image load handlers ----
  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setError(null);
  }, []);

  const handleImageError = useCallback(() => {
    setError('No se pudo cargar la imagen.');
    setIsLoading(false);
  }, []);

  // Reset state when fileUrl changes
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setNumPages(0);
    setCurrentPage(1);
    setScale(ZOOM_DEFAULT);
    setRotation(0);
    setFitWidthScale(null);
  }, [fileUrl]);

  const zoomPercent = Math.round(scale * 100);

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <div
      ref={containerRef}
      className="flex flex-col w-full h-full bg-calm-bg rounded-huge overflow-hidden border border-calm-border"
    >
      {/* ─── Toolbar ─── */}
      <div className="flex items-center justify-between px-3 py-2 bg-calm-bg border-b border-calm-border flex-shrink-0 gap-2">
        {/* Left: Page navigation */}
        <div className="flex items-center gap-1">
          {!isImage && numPages > 0 && (
            <>
              <ToolbarButton
                onClick={goToPrevPage}
                disabled={currentPage <= 1}
                title="Página anterior"
              >
                <Icon name="navigate_before" className="text-lg" />
              </ToolbarButton>

              <span className="text-sm font-light text-calm-text tabular-nums min-w-[80px] text-center select-none">
                <span className="text-calm-muted">Pág</span>{' '}
                <span className="font-medium">{currentPage}</span>{' '}
                <span className="text-calm-muted">de</span>{' '}
                <span className="font-medium">{numPages}</span>
              </span>

              <ToolbarButton
                onClick={goToNextPage}
                disabled={currentPage >= numPages}
                title="Página siguiente"
              >
                <Icon name="navigate_next" className="text-lg" />
              </ToolbarButton>
            </>
          )}
          {isImage && (
            <span className="flex items-center gap-1.5 text-sm font-light text-calm-muted">
              <Icon name="image" className="text-base" />
              Imagen
            </span>
          )}
        </div>

        {/* Center: Zoom controls */}
        <div className="flex items-center gap-1">
          <ToolbarButton onClick={handleZoomOut} disabled={scale <= ZOOM_MIN} title="Alejar">
            <Icon name="zoom_out" className="text-base" />
          </ToolbarButton>

          <span className="text-xs font-light text-calm-muted min-w-[40px] text-center tabular-nums select-none">
            {zoomPercent}%
          </span>

          <ToolbarButton onClick={handleZoomIn} disabled={scale >= ZOOM_MAX} title="Acercar">
            <Icon name="zoom_in" className="text-base" />
          </ToolbarButton>

          <ToolbarButton onClick={handleFitWidth} title="Ajustar al ancho">
            <Icon name="fit_screen" className="text-base" />
          </ToolbarButton>

          <ToolbarButton onClick={handleZoomReset} title="Zoom 100%">
            <span className="text-[10px] font-medium text-calm-muted leading-none">1:1</span>
          </ToolbarButton>
        </div>

        {/* Right: Rotate + Download */}
        <div className="flex items-center gap-1">
          <ToolbarButton onClick={handleRotate} title="Rotar 90°">
            <Icon name="rotate_right" className="text-base" />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={handleDownload} title="Descargar">
            <Icon name="download" className="text-base" />
          </ToolbarButton>
        </div>
      </div>

      {/* ─── Viewer area ─── */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto bg-slate-100 relative"
      >
        {/* Loading skeleton */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10">
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl border-2 border-calm-border border-t-calm-text animate-spin" />
            </div>
            <p className="text-sm font-light text-calm-muted animate-pulse-soft">
              Cargando documento…
            </p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10 bg-slate-100">
            <div className="flex flex-col items-center gap-3 max-w-sm text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                <Icon name="error" className="text-2xl text-red-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-calm-text mb-1">
                  Error al cargar el documento
                </p>
                <p className="text-xs font-light text-calm-muted leading-relaxed">
                  {error}
                </p>
              </div>
              <button
                type="button"
                onClick={handleRetry}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-light text-calm-text bg-white border border-calm-border rounded-xl hover:bg-slate-50 transition-colors"
              >
                <Icon name="refresh" className="text-base" />
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* PDF rendering */}
        {!isImage && !error && (
          <div className="flex justify-center py-6 px-6 min-h-full">
            <Document
              key={fileUrl}
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
              error={null}
              className="flex flex-col items-center gap-4"
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                rotate={rotation}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                onRenderSuccess={handlePageRenderSuccess}
                loading={null}
                className={cn(
                  'shadow-card-hover rounded-sm transition-shadow duration-300',
                  isLoading && 'opacity-0',
                )}
              />
            </Document>
          </div>
        )}

        {/* Image rendering */}
        {isImage && !error && (
          <div className="flex items-center justify-center min-h-full py-6 px-6">
            <img
              src={fileUrl}
              alt={displayName}
              onLoad={handleImageLoad}
              onError={handleImageError}
              className={cn(
                'max-w-full object-contain rounded-sm shadow-card-hover transition-all duration-300',
                isLoading && 'opacity-0',
              )}
              style={{
                transform: `scale(${scale}) rotate(${rotation}deg)`,
                transformOrigin: 'center center',
              }}
              draggable={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SSR-safe export — only render on client
// ---------------------------------------------------------------------------
function PdfViewerGuard(props: PdfViewerProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="flex flex-col w-full h-full bg-calm-bg rounded-huge overflow-hidden border border-calm-border">
        <div className="flex items-center px-3 py-2 bg-calm-bg border-b border-calm-border">
          <span className="text-sm font-light text-calm-muted">Cargando visor…</span>
        </div>
        <div className="flex-1 bg-slate-100 flex items-center justify-center">
          <div className="w-10 h-10 rounded-2xl border-2 border-calm-border border-t-calm-text animate-spin" />
        </div>
      </div>
    );
  }

  return <PdfViewer {...props} />;
}

export default PdfViewerGuard;
